import { Action, Middleware } from "@reduxjs/toolkit";
import {
  lyfshortLogin,
  lyfshortLoginSuccess,
  loginEmailUnverified,
  lyfshortLoginFailed,
  loginLyfshortFetchedUser,
  loginMissingMinimumUserDetails,
  loginSetConfirmDetails,
} from "../slices/auth/authSlice";
import {
  getUserSignin,
  updateUser as updateUserAPI,
  checkUserEmailVerification as checkUserEmailVerificationAPI,
} from "../../utils/lyfshortService";
import { sendEmailVerification } from "../../utils/firebase";
import { checkUserEmailVerification, checkUserEmailVerificationFailed, checkUserEmailVerificationSuccess, updateUser, updateUserFailed, updateUserSuccess } from "../slices/user/userSlice";
import { getGoogleProfile } from "../../utils/google";
import { userSchema } from "../../utils/userSchemas";
import { ValidationError } from "yup";
import { GoogleProfile, LyfshortUserProfile } from '../../types/authState'

type Updates = Partial<LyfshortUserProfile>
type PrimaryFieldNames = 'firstName' | 'lastName' | 'homeLocationCountry' | 'mobilePhone' | 'birthDate'

const getMissingUserDetailsFromSuggestions = (
  user: LyfshortUserProfile,
  suggestions: Partial<LyfshortUserProfile>
): Updates => {
  const updates: Updates = {};
  const fieldNames: PrimaryFieldNames[] = [
    'lastName',
    'firstName',
    'homeLocationCountry',
    'mobilePhone',
    'birthDate',
  ];

  for (const fieldName of fieldNames) {
    if (!user[fieldName] && suggestions[fieldName]) {
      updates[fieldName] = suggestions[fieldName];
    }
  }

  if (!user.sex && suggestions.sex) {
    updates.sex = suggestions.sex;
  }

  return updates;
};

const filterInvalidUserDetails = (userDetails: LyfshortUserProfile): LyfshortUserProfile => {
  try {
    // Validate the given details.
    userSchema.validateSync(userDetails, {
      strict: true,
      abortEarly: false,
    });

    // All fields were valid.
    return userDetails;
  } catch (error) {
    if (error instanceof ValidationError && error.inner) {
      const filteredDetails: LyfshortUserProfile = { ...userDetails };

      for (const fieldError of error.inner) {
        if (fieldError.path) {
          delete filteredDetails[fieldError.path as keyof LyfshortUserProfile];
        }
      }

      return filteredDetails;
    } else {
      throw error;
    }
  }
};

const getUserDetailsFromGoogle = async (googleAccessToken: string): Promise<LyfshortUserProfile> => {
  const details: LyfshortUserProfile = {} as LyfshortUserProfile;
  // Get Google profile
  return getGoogleProfile(googleAccessToken)
    .then((profile: GoogleProfile) => {
      console.log(`getUserDetailsFromGoogle data`, profile);

      // Pick out details
      if (profile.family_name) {
        details.lastName = profile.family_name;
      }

      if (profile.given_name) {
        details.firstName = profile.given_name;
      }

      if (!details.firstName && profile.name) {
        let nameParts = String(profile.name).trim().split(' ');
        if (nameParts.length > 0) {
          details.firstName = nameParts.shift();
          details.lastName = nameParts.join(' ').trim();
        }
      }
      if (profile.gender === 'male' || profile.gender === 'female') {
        details.sex = profile.gender.charAt(0) as 'm' | 'f';
      }
      return details;
    })
}

const loginCheckUser = async (store: any, next: any, action: any) => { //FIXME: Correct types
  try {
    const state = store.getState();
    const user = state.user.lyfshortUser;
    // Check that the user meets teh requirements before continuing with
    // the login process
    // TODO make sure the userSchema.isValid check is future compatible, it can handle new unknown fields
    const isAppleSignin = !!state.auth.tempData.signupData.appleAccessToken

    if (state.auth.shouldConfirmDetails
      || !userSchema.isValidSync(
        user,
        { context: { isNameOptional: isAppleSignin } }
      )) {
      store.dispatch(loginMissingMinimumUserDetails())
    } else if (!(user as any)?.emailVerified) {
      if (checkUserEmailVerificationSuccess.match(action)) {
        setTimeout(() => {
          store.dispatch(checkUserEmailVerification({
            requiresAccessToken: true,
          }))
        }, 5000);
      } else {
        store.dispatch(checkUserEmailVerification({
          requiresAccessToken: true
        }))
        store.dispatch(loginEmailUnverified())
      }
    } else {
      store.dispatch(lyfshortLoginSuccess({
        user: state.user.lyfshortUser,
        tokens: state.auth.tempData.authData.tokens,
        action: state.auth.tempData.signupData
      }))
    }
  } catch (error) {
    console.warn(error)
    store.dispatch(lyfshortLoginFailed(error))
  }
}

const loginHandler = async (store: any, next: any, action: any) => { //FIXME: Correct types
  try {
    const signupData = (action.payload.signupData && { ...action.payload.signupData }) || {}
    // Get the user profile
    const userResult = await getUserSignin(action.payload.authData.tokens.accessToken)
      .catch((err) => {
        console.error('A problem happened while getting your profile')
        throw err
      })

    const user = userResult.user;
    const pendingUserUpdate = { ...user }
    let updatesFromSignupData = {};
    let updatesFromIDProvider = {};
    let updatesFromDevice = {};
    // Send verification email if user is new and not already verified.
    if (userResult.isNewUser && !user.emailVerified) {
      try {
        sendEmailVerification()
      } catch (error) {
        console.warn(error)
      }
    }
    // Apply signup data to user
    // Pick user fields out of signupData
    const signupUserFields = [
      'firstName',
      'lastName',
      'email',
      'username',
      'homeLocationCountry',
      'mobilePhone',
      'sex',
      'birthDate'
    ];
    updatesFromSignupData = signupUserFields.reduce(
      (accum, key) => {
        if (signupData[key]) {
          return {
            ...accum,
            [key]: signupData[key]
          }
        } else {
          return accum;
        }
      },
      {}
    );

    Object.assign(pendingUserUpdate, updatesFromSignupData)

    if (signupData.googleAccessToken) {
      // Get user details from google
      try {
        const googleData = await getUserDetailsFromGoogle(action.payload.signupData.googleAccessToken)
        updatesFromIDProvider = getMissingUserDetailsFromSuggestions(
          pendingUserUpdate,
          filterInvalidUserDetails(googleData)
        )
        Object.assign(pendingUserUpdate, updatesFromIDProvider);
        if (googleData.externalProfilePicture) {
          signupData.externalProfilePicture = googleData.externalProfilePicture;
        }
      } catch (error) {
        console.warn(error);
      }
    }

    // Save signup data to profile if needed
    if (Object.keys(updatesFromSignupData).length > 0
      || (Object.keys(updatesFromIDProvider).length > 0)
      || (Object.keys(updatesFromDevice).length > 0)
    ) {
      store.dispatch(updateUser(pendingUserUpdate))
    }
    // Show account setup to new IDP users regardless of valid user schema
    // allowing them to confirm their details
    if (userResult.isNewUser && (
      signupData.googleAccessToken
      || signupData.appleAccessToken
    )) {
      store.dispatch(loginSetConfirmDetails(true))
    }
    store.dispatch(loginLyfshortFetchedUser({
      authData: action.payload.authData,
      signupData: { ...signupData, isNewUser: !!userResult.isNewUser },
      user: pendingUserUpdate
    }))
  } catch (error) {
    console.warn(error)
    store.dispatch(lyfshortLoginFailed(error))
  }
}

const lyfshortServiceMiddleware: Middleware = (store) => (next) => async (action) => {
  const state = store.getState()

  // Pass the action along the middleware chain
  next(action);

  // Process actions that do not require being logged-in
  if (lyfshortLogin.match(action)) {
    await loginHandler(store, next, action);
  } else if (loginLyfshortFetchedUser.match(action)) {
    await loginCheckUser(store, next, action)
  } else if (checkUserEmailVerificationSuccess.match(action) || updateUserSuccess.match(action)) {
    if (state.auth.progress == 'get-user-details' || state.auth.progress == 'verify-email') {
      await loginCheckUser(store, next, action)
    }
  }

  const sameUserDispatch = (newAction: Action) => {
    // Comparing the state from the start of this middleware invocation to
    // the current state, only dispatch the given action if the current
    // user is the same as when this middleware invocation started.
    const currentState = store.getState()
    if (state.user.lyfshortUser && currentState.user.lyfshortUser && (state.user.lyfshortUser.id === currentState.user.lyfshortUser.id)) {
      store.dispatch(newAction);
    }
  }

  // Process actions that require being logged-in or having an access-token
  // while in the middle of logging in.
  if (state.auth.isLyfshortLoggedIn || state.auth.updating && state.accessToken.body) {
    if (updateUser.match(action)) {
      updateUserAPI(action.payload, state.accessToken.body)
        .catch(err => {
          sameUserDispatch(updateUserFailed(err))
          throw err
        }).then((user) => {
          sameUserDispatch(updateUserSuccess(user));
        }).catch(err => console.warn(err))
    } else if (checkUserEmailVerification.match(action)) {
      checkUserEmailVerificationAPI(state.accessToken.body)
        .catch(error => {
          sameUserDispatch(checkUserEmailVerificationFailed(error))
          throw error
        })
        .then((result) => {
          sameUserDispatch(checkUserEmailVerificationSuccess(result))
          if (result.verified) {
            // TODO: Try to close the verify email modal in case it is open.
          }
        })
        .catch(err => console.warn(err))
    }
  }

  // Skip the rest of this middleware if not logged-in
  if (!state.auth.isLyfshortLoggedIn) {
    return
  }
}

export default lyfshortServiceMiddleware